import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import AIChatMessagesList from './AIChatMessagesList';
import AIMessageInput from './AIMessageInput';
import ZenSpinner from '../ZenSpinner';

const AIChat = ({ onMessageSent }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchSessionAndHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Token manquant');

        const sessionResponse = await fetch(`${backendUrl}/api/ai/start-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: currentUser.userId,
            sessionType: 'chat',
          }),
        });

        if (!sessionResponse.ok) throw new Error("Erreur création session");

        const sessionData = await sessionResponse.json();
        setSessionId(sessionData.sessionId);

        const historyRes = await fetch(`${backendUrl}/api/ai/session-history/${sessionData.sessionId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!historyRes.ok) throw new Error("Erreur récupération historique");

        const historyData = await historyRes.json();
        const allMessages = historyData.flatMap(m =>
          m.messages.map(msg => ({
            ...msg,
            sender: msg.sender === 'user' ? currentUser.userId : 'AI',
            timestamp: new Date(msg.timestamp),
          }))
        );

        setMessages(allMessages.sort((a, b) => a.timestamp - b.timestamp));
      } catch (err) {
        // console.error('Erreur chargement session:', err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessionAndHistory();
  }, [currentUser.userId]);

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    const userMessage = { sender: currentUser.userId, message, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      setIsTyping(true);

      let currentMeetingId = meetingId;
      if (!currentMeetingId) {
        const meetingRes = await fetch(`${backendUrl}/api/ai/start-meeting`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ sessionId }),
        });

        if (!meetingRes.ok) throw new Error('Erreur création meeting');

        const meetingData = await meetingRes.json();
        currentMeetingId = meetingData.meetingId;
        setMeetingId(currentMeetingId);
      }

      const response = await fetch(`${backendUrl}/api/ai/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId,
          meetingId: currentMeetingId,
          message,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur communication IA');
      }

      const data = await response.json();
      const aiResponse = data.message;

      setMessages(prev => [
        ...prev,
        { sender: 'AI', message: aiResponse, timestamp: new Date() },
      ]);

      // 👉 Déclenche la mise à jour des crédits
      if (typeof onMessageSent === 'function') {
        onMessageSent();
      }

    } catch (err) {
      console.error('Erreur envoi message:', err.message);
    } finally {
      setIsTyping(false);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 1)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ZenSpinner />
        </Box>
      )}

      <Box sx={{ flexGrow: 1, overflowY: 'auto', pb: 9, pt: 3 }}>
        <AIChatMessagesList
          messages={messages}
          currentUser={currentUser}
          messagesEndRef={messagesEndRef}
          isTyping={isTyping}
        />
      </Box>

      <AIMessageInput
        onSendMessage={sendMessage}
        onMessageSent={onMessageSent}
        placeholder="Écris un message..."
        disabled={isTyping || isLoading}
      />
    </Box>
  );
};

export default AIChat;
